import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import AdminStatCard from "../../components/AdminStatCard";
import { getGlobalPerformance, getAllAuditLogs } from "../../api/services";
import {
  Users, Building2, TrendingUp, CheckCircle2,
  Activity, CheckSquare, MessageSquare,
  AlertCircle, RefreshCw, UserCheck, Mail, Phone, StickyNote,
} from "lucide-react";

/* ── Skeleton ── */
function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-neutral-100 rounded-lg ${className}`} />;
}

/* ── Avatar helpers ── */
const AV_COLORS = [
  ["#eef3fd","#009DB9"], ["#f0fdf4","#16a34a"], ["#eff6ff","#3b82f6"],
  ["#fff7ed","#ea580c"], ["#fdf4ff","#a21caf"], ["#f1f5f9","#475569"],
];
function avColor(name) { return AV_COLORS[(name?.charCodeAt(0) ?? 0) % AV_COLORS.length]; }

function fmtDT(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  const diffDays = Math.floor((new Date() - d) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Yesterday";
  return `${diffDays}d ago`;
}

/* ── Role label ── */
function roleLabel(role) {
  const map = {
    SUPER_ADMIN: "Super Admin", ADMIN: "Admin", MANAGER: "Manager",
    SALES_REP: "Sales Rep", EMPLOYEE: "Employee", MEMBER: "Member", VIEWER: "Viewer",
  };
  return map[role] ?? role;
}
function roleBadgeStyle(role) {
  if (role === "SUPER_ADMIN" || role === "ADMIN") return { bg: "#eef3fd", color: "#009DB9" };
  if (role === "MANAGER") return { bg: "#f0fdf4", color: "#16a34a" };
  if (role === "SALES_REP") return { bg: "#eff6ff", color: "#3b82f6" };
  return { bg: "#f3f4f6", color: "#374151" };
}

/* ── Deals by stage bar chart (horizontal) ── */
function StageChart({ dealsByStage }) {
  if (!dealsByStage) return null;
  const entries = Object.entries(dealsByStage).filter(([, v]) => v > 0);
  const max     = Math.max(...Object.values(dealsByStage), 1);
  const STAGE_COLORS = {
    PROSPECTING: "#8B5CF6", LEAD_QUALIFICATION: "#EC4899",
    DISCOVERY_PAIN_POINTS: "#3B82F6", DEMO_PITCH: "#F59E0B",
    PROPOSAL: "#009DB9", NEGOTIATION: "#EF4444",
    CLOSED_WON: "#10B981", CLOSED_LOST: "#6B7280",
  };
  const labelMap = {
    PROSPECTING: "Prospecting", LEAD_QUALIFICATION: "Lead Qual.",
    DISCOVERY_PAIN_POINTS: "Discovery", DEMO_PITCH: "Demo",
    PROPOSAL: "Proposal", NEGOTIATION: "Negotiation",
    CLOSED_WON: "Closed Won", CLOSED_LOST: "Closed Lost",
  };
  const allZero = Object.values(dealsByStage).every(v => v === 0);
  if (allZero) return (
    <div className="flex items-center justify-center py-8 text-[12px] text-neutral-400">No deals in pipeline yet</div>
  );
  return (
    <div className="flex flex-col gap-2.5 px-5 py-4">
      {Object.entries(dealsByStage).map(([stage, count]) => {
        const pct   = (count / max) * 100;
        const color = STAGE_COLORS[stage] ?? "#009DB9";
        return (
          <div key={stage} className="flex items-center gap-3">
            <span className="text-[11px] text-neutral-500 w-24 flex-shrink-0 truncate">{labelMap[stage] ?? stage}</span>
            <div className="flex-1 h-2 overflow-hidden rounded-full bg-neutral-100">
              <div className="h-full transition-all duration-500 rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="text-[12px] font-semibold text-neutral-700 w-4 text-right flex-shrink-0">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Interactions by type mini chart ── */
function InteractionChart({ interactionsByType }) {
  if (!interactionsByType) return null;
  const items = [
    { key: "CALL",    label: "Calls",    icon: Phone,      color: "#16a34a", bg: "#f0fdf4" },
    { key: "EMAIL",   label: "Emails",   icon: Mail,       color: "#3b82f6", bg: "#eff6ff" },
    { key: "MEETING", label: "Meetings", icon: UserCheck,  color: "#8b5cf6", bg: "#ede9fe" },
    { key: "NOTE",    label: "Notes",    icon: StickyNote, color: "#d97706", bg: "#fffbeb" },
  ];
  return (
    <div className="grid grid-cols-1 gap-2 px-5 py-4 sm:grid-cols-2">
      {items.map(({ key, label, icon: Icon, color, bg }) => (
        <div key={key} className="flex items-center gap-2.5 p-3 rounded-xl" style={{ background: bg }}>
          <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg"
            style={{ background: color + "22" }}>
            <Icon size={14} strokeWidth={2} style={{ color }} />
          </div>
          <div>
            <div className="text-[18px] font-bold leading-none" style={{ color }}>{interactionsByType[key] ?? 0}</div>
            <div className="text-[10px] text-neutral-500 mt-0.5">{label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminOverview() {
  const navigate = useNavigate();

  const [perf,    setPerf]    = useState(null);
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [perfData, logsData] = await Promise.all([
          getGlobalPerformance(),
          getAllAuditLogs({ page: 0, size: 5 }),
        ]);
        setPerf(perfData);
        const items = Array.isArray(logsData) ? logsData : (logsData.content ?? []);
        setLogs(items.slice(0, 5));
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load overview");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (error) return (
    <AdminLayout title="Overview" subtitle="System-wide health & performance">
      <div className="flex flex-col items-center justify-center gap-3 py-20">
        <AlertCircle size={32} className="text-red-400" strokeWidth={1.5} />
        <p className="text-[14px] font-semibold text-neutral-700">Failed to load overview</p>
        <p className="text-[12px] text-neutral-400">{error}</p>
        <button onClick={() => window.location.reload()}
          className="flex items-center gap-1.5 mt-2 px-4 h-9 rounded-lg text-[13px] font-semibold border border-neutral-200 hover:bg-neutral-50">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    </AdminLayout>
  );

  /* ── Map actual API fields to stat cards ── */
  const stats = perf ? [
    {
      icon: Users,
      label: "Total Users",
      value: String(perf.totalUsers ?? "—"),
      trend: null, trendDir: null,
      sub: `${perf.activeUsers ?? 0} active`,
    },
    {
      icon: Building2,
      label: "Total Contacts",
      value: String(perf.totalContacts ?? "—"),
      trend: null, trendDir: null,
      sub: `${Object.values(perf.contactsByStatus ?? {}).reduce((a, b) => a + b, 0)} across all reps`,
    },
    {
      icon: TrendingUp,
      label: "Total Deals",
      value: String(perf.totalDeals ?? "—"),
      trend: null, trendDir: null,
      sub: `${perf.wonDeals ?? 0} won · ${perf.lostDeals ?? 0} lost`,
    },
    {
      icon: CheckSquare,
      label: "Total Tasks",
      value: String(perf.totalTasks ?? "—"),
      trend: perf.overdueTasks > 0 ? `${perf.overdueTasks} overdue` : null,
      trendDir: perf.overdueTasks > 0 ? "down" : null,
      sub: `${perf.completedTasks ?? 0} completed`,
    },
    {
      icon: MessageSquare,
      label: "Interactions",
      value: String(perf.totalInteractions ?? "—"),
      trend: null, trendDir: null,
      sub: "calls, emails, meetings",
    },
    {
      icon: CheckCircle2,
      label: "Tasks Done",
      value: perf.totalTasks > 0
        ? `${Math.round(((perf.completedTasks ?? 0) / perf.totalTasks) * 100)}%`
        : "0%",
      trend: null, trendDir: null,
      sub: "completion rate",
    },
  ] : null;

  /* ── User breakdown from userBreakdown array ── */
  const userBreakdown = perf?.userBreakdown ?? [];
  const activeCount   = userBreakdown.filter(u => u.active).length;
  const inactiveCount = userBreakdown.filter(u => !u.active).length;

  /* ── Role counts derived from userBreakdown ── */
  const roleCounts = userBreakdown.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] ?? 0) + 1;
    return acc;
  }, {});

  /* ── Contact status breakdown ── */
  const contactsByStatus = perf?.contactsByStatus ?? {};

  return (
    <AdminLayout title="Overview" subtitle="System-wide health & performance">

      {/* ── 6 Stat cards ── */}
      <div className="grid gap-3.5 mb-5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="px-5 py-4 bg-white border border-neutral-100 rounded-xl" style={{ boxShadow: "0 1px 3px rgba(17,24,39,.08)" }}>
                <Skeleton className="w-24 h-3 mb-3" />
                <Skeleton className="w-16 h-8 mb-2" />
                <Skeleton className="w-20 h-3" />
              </div>
            ))
          : stats.map((s, i) => <AdminStatCard key={i} {...s} />)
        }
      </div>

      {/* ── Row 2: Pipeline stages + Interactions - STACK ON MOBILE ── */}
      <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2">
        {/* Deals by stage */}
        <div className="bg-white border border-neutral-100 rounded-xl" style={{ boxShadow: "0 1px 3px rgba(17,24,39,.08)" }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
            <div>
              <div className="text-[14px] font-bold text-neutral-900">Deals by Stage</div>
              <div className="text-[11px] text-neutral-400 mt-0.5">{perf?.totalDeals ?? 0} total deals</div>
            </div>
            <button onClick={() => navigate("/pipeline")}
              className="text-[11px] font-semibold" style={{ color: "#009DB9" }}>View →</button>
          </div>
          {loading
            ? <div className="flex flex-col gap-2.5 px-5 py-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="w-full h-4" />)}</div>
            : <StageChart dealsByStage={perf?.dealsByStage} />
          }
        </div>

        {/* Interactions by type */}
        <div className="bg-white border border-neutral-100 rounded-xl" style={{ boxShadow: "0 1px 3px rgba(17,24,39,.08)" }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
            <div>
              <div className="text-[14px] font-bold text-neutral-900">Interactions</div>
              <div className="text-[11px] text-neutral-400 mt-0.5">{perf?.totalInteractions ?? 0} total</div>
            </div>
          </div>
          {loading
            ? <div className="grid grid-cols-1 gap-2 px-5 py-4 sm:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="w-full h-16 rounded-xl" />)}</div>
            : <InteractionChart interactionsByType={perf?.interactionsByType} />
          }
        </div>
      </div>

      {/* ── Row 3: User breakdown + Contact status - STACK ON MOBILE ── */}
      <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2">

        {/* User status + roles */}
        <div className="bg-white border border-neutral-100 rounded-xl" style={{ boxShadow: "0 1px 3px rgba(17,24,39,.08)" }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
            <div className="text-[14px] font-bold text-neutral-900">User Breakdown</div>
            <button onClick={() => navigate("/admin/users")}
              className="text-[11px] font-semibold" style={{ color: "#009DB9" }}>View all →</button>
          </div>
          <div className="flex flex-col gap-2 px-5 py-4">
            {loading
              ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="w-full h-10" />)
              : (
                <>
                  {[
                    { label: "Active",   count: activeCount,   bg: "#f0fdf4", color: "#16a34a" },
                    { label: "Inactive", count: inactiveCount, bg: "#fef2f2", color: "#dc2626" },
                  ].map((r) => (
                    <div key={r.label} className="flex items-center justify-between px-3 py-2.5 rounded-lg" style={{ background: r.bg }}>
                      <span className="text-[13px] font-medium" style={{ color: r.color }}>{r.label}</span>
                      <span className="text-[20px] font-bold tabular-nums" style={{ color: r.color }}>{r.count}</span>
                    </div>
                  ))}
                  <div className="pt-3 mt-1 border-t border-neutral-100">
                    <div className="text-[11px] font-semibold text-neutral-400 uppercase tracking-[.07em] mb-2">By Role</div>
                    {Object.entries(roleCounts).map(([role, count]) => {
                      const s = roleBadgeStyle(role);
                      return (
                        <div key={role} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                          <span className="inline-flex items-center text-[11px] font-semibold rounded-full px-2.5 py-0.5"
                            style={{ background: s.bg, color: s.color }}>{roleLabel(role)}</span>
                          <span className="text-[13px] font-semibold text-neutral-900">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )
            }
          </div>
        </div>

        {/* Contacts by status */}
        <div className="bg-white border border-neutral-100 rounded-xl" style={{ boxShadow: "0 1px 3px rgba(17,24,39,.08)" }}>
          <div className="px-5 py-4 border-b border-neutral-100">
            <div className="text-[14px] font-bold text-neutral-900">Contacts by Status</div>
            <div className="text-[11px] text-neutral-400 mt-0.5">{perf?.totalContacts ?? 0} total contacts</div>
          </div>
          <div className="px-5 py-4 flex flex-col gap-2.5">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="w-full h-4" />)
              : Object.entries(contactsByStatus).map(([status, count]) => {
                  const colorMap = {
                    LEAD: "#8b5cf6", PROSPECT: "#009DB9", CUSTOMER: "#16a34a",
                    WON: "#10b981", PROPOSAL: "#f59e0b",
                  };
                  const color = colorMap[status] ?? "#6b7280";
                  const total = Object.values(contactsByStatus).reduce((a, b) => a + b, 0) || 1;
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <span className="text-[11px] text-neutral-500 w-20 flex-shrink-0 capitalize">{status.toLowerCase()}</span>
                      <div className="flex-1 h-2 overflow-hidden rounded-full bg-neutral-100">
                        <div className="h-full transition-all duration-500 rounded-full" style={{ width: `${(count / total) * 100}%`, background: color }} />
                      </div>
                      <span className="text-[12px] font-semibold text-neutral-700 w-4 text-right flex-shrink-0">{count}</span>
                    </div>
                  );
                })
            }
          </div>
        </div>
      </div>

      {/* ── Row 4: User performance table - RESPONSIVE ── */}
      <div className="mb-5 bg-white border border-neutral-100 rounded-xl" style={{ boxShadow: "0 1px 3px rgba(17,24,39,.08)" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <div className="text-[14px] font-bold text-neutral-900">Team Performance</div>
          <button onClick={() => navigate("/admin/users")}
            className="text-[11px] font-semibold" style={{ color: "#009DB9" }}>View all →</button>
        </div>
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-5 py-3.5 border-b border-neutral-100 last:border-0">
                <Skeleton className="flex-shrink-0 w-8 h-8 rounded-full" />
                <Skeleton className="flex-1 w-full h-4" />
                <Skeleton className="w-24 h-4" />
              </div>
            ))
          : userBreakdown.length === 0
          ? <div className="py-10 text-center text-[13px] text-neutral-400">No users found.</div>
          : userBreakdown.map((u) => {
              const [bg, fg] = avColor(u.firstName ?? "U");
              const ini = u.firstName && u.lastName
                ? `${u.firstName[0]}${u.lastName[0]}`.toUpperCase()
                : (u.email ?? "?").substring(0, 2).toUpperCase();
              const rs = roleBadgeStyle(u.role);
              return (
                <div key={u.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60 transition-colors">
                  {/* Avatar */}
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                      style={{ background: bg, color: fg }}>{ini}</div>

                    {/* Name + role - stacked on mobile */}
                    <div className="flex-1 min-w-0 sm:hidden">
                      <div className="text-[13px] font-semibold text-neutral-900">{u.fullName ?? `${u.firstName} ${u.lastName}`}</div>
                      <div className="text-[11px] text-neutral-400">{u.email}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5"
                          style={{ background: rs.bg, color: rs.color }}>{roleLabel(u.role)}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5"
                          style={{ background: u.active ? "#f0fdf4" : "#fef2f2", color: u.active ? "#16a34a" : "#dc2626" }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: u.active ? "#16a34a" : "#dc2626" }} />
                          {u.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Desktop view - hidden on mobile */}
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[13px] font-semibold text-neutral-900 truncate">{u.fullName ?? `${u.firstName} ${u.lastName}`}</span>
                        <span className="inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5"
                          style={{ background: rs.bg, color: rs.color }}>{roleLabel(u.role)}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5"
                          style={{ background: u.active ? "#f0fdf4" : "#fef2f2", color: u.active ? "#16a34a" : "#dc2626" }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: u.active ? "#16a34a" : "#dc2626" }} />
                          {u.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="text-[11px] text-neutral-400 mt-0.5">{u.email}</div>
                    </div>

                    {/* Stats - hidden on mobile */}
                    <div className="flex items-center flex-shrink-0 gap-4">
                      {[
                        { label: "Deals",    value: u.totalDeals    ?? 0 },
                        { label: "Won",      value: u.wonDeals      ?? 0 },
                        { label: "Tasks",    value: u.totalTasks    ?? 0 },
                        { label: "Interact", value: u.totalInteractions ?? 0 },
                      ].map(({ label, value }) => (
                        <div key={label} className="text-center">
                          <div className="text-[15px] font-bold text-neutral-900 tabular-nums leading-none">{value}</div>
                          <div className="text-[10px] text-neutral-400 mt-0.5">{label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Last login - hidden on mobile */}
                    <div className="flex-shrink-0 text-right">
                      <div className="text-[11px] text-neutral-400">Last login</div>
                      <div className="text-[12px] font-medium text-neutral-700">{fmtDT(u.lastLogin)}</div>
                    </div>
                  </div>

                  {/* Mobile stats row */}
                  <div className="flex items-center justify-between pt-2 border-t sm:hidden border-neutral-100">
                    <div className="flex gap-3">
                      <div>
                        <div className="text-[14px] font-bold text-neutral-900">{u.totalDeals ?? 0}</div>
                        <div className="text-[9px] text-neutral-400">Deals</div>
                      </div>
                      <div>
                        <div className="text-[14px] font-bold text-neutral-900">{u.wonDeals ?? 0}</div>
                        <div className="text-[9px] text-neutral-400">Won</div>
                      </div>
                      <div>
                        <div className="text-[14px] font-bold text-neutral-900">{u.totalTasks ?? 0}</div>
                        <div className="text-[9px] text-neutral-400">Tasks</div>
                      </div>
                      <div>
                        <div className="text-[14px] font-bold text-neutral-900">{u.totalInteractions ?? 0}</div>
                        <div className="text-[9px] text-neutral-400">Int.</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-neutral-400">Last login</div>
                      <div className="text-[11px] font-medium text-neutral-700">{fmtDT(u.lastLogin)}</div>
                    </div>
                  </div>
                </div>
              );
            })
        }
      </div>

      {/* ── Recent audit activity - RESPONSIVE ── */}
      {logs.length > 0 && (
        <div className="bg-white border border-neutral-100 rounded-xl" style={{ boxShadow: "0 1px 3px rgba(17,24,39,.08)" }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
            <div className="text-[14px] font-bold text-neutral-900">Recent System Activity</div>
            <button onClick={() => navigate("/admin/logs")}
              className="text-[11px] font-semibold" style={{ color: "#009DB9" }}>View all →</button>
          </div>
          {logs.map((log, i) => (
            <div key={log.id ?? i} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-5 py-3.5 border-b border-neutral-100 last:border-0">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center flex-shrink-0 rounded-lg w-7 h-7 bg-neutral-100">
                  <Activity size={13} strokeWidth={2} className="text-neutral-500" />
                </div>
                <span className="flex-1 text-[13px] text-neutral-700">{log.detail ?? log.description ?? log.action}</span>
              </div>
              <span className="text-[11px] text-neutral-400 sm:text-right sm:ml-auto">{fmtDT(log.timestamp)}</span>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}